import DeleteFolderPopup from "@/features/list/components/DeleteFolderPopup";
import EditFolderNamePopup from "@/features/list/components/EditFolderNamePopup";
import useWorkflowStore from "@/store/workflow/workflowStore";
import useFolderStore from "@/store/ui/folderStore";
import type { CustomTheme } from "@/theme/theme";
import type { Workflow } from "@/types/resources";
import { MoreVert } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Collapse,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import type { ReactNode } from "react";
import { Fragment, useState } from "react";
import { useScrollPosition } from "../hooks/useScrollPosition";

type Props = {
  workflowItemRenderer: (args: { workflow: Workflow }) => ReactNode;
  folder: {
    id: string;
    name: string;
    workflows: Workflow[];
  };
};

export const FolderItem: React.FC<Props> = ({
  folder,
  workflowItemRenderer,
}) => {
  const { isFolderOpen, toggleFolder } = useFolderStore();
  const isOpen = isFolderOpen(folder.id);
  const theme: CustomTheme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const { openAddRulePopup } = useWorkflowStore();
  const { saveScrollPosition } = useScrollPosition();

  const [isDeleteFolderPopupOpen, setIsDeleteFolderPopupOpen] = useState(false);
  const [isEditFolderNamePopupOpen, setIsEditFolderNamePopupOpen] =
    useState(false);

  const handleFolderClick = () => {
    saveScrollPosition();
    toggleFolder(folder.id);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    saveScrollPosition();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    saveScrollPosition();
    setIsDeleteFolderPopupOpen(true);
    handleMenuClose();
  };

  const handleEditClick = () => {
    saveScrollPosition();
    setIsEditFolderNamePopupOpen(true);
    handleMenuClose();
  };

  const handleCloseDeleteFolderPopup = () => {
    setIsDeleteFolderPopupOpen(false);
  };

  const handleCloseEditFolderNamePopup = () => {
    setIsEditFolderNamePopupOpen(false);
  };

  const handleAddRule = () => {
    if (!folder.id) {
      return;
    }
    saveScrollPosition();
    openAddRulePopup(folder.id);
    handleMenuClose();
  };

  return (
    <Box>
      <ListItem
        secondaryAction={
          <IconButton
            edge="end"
            aria-label="comments"
            onClick={handleMenuClick}
          >
            <MoreVert />
          </IconButton>
        }
        disablePadding
      >
        <ListItemButton
          onClick={handleFolderClick}
          sx={{
            backgroundColor: "#dddddd",
            padding: "8px 20px",
            borderRadius: "10px",
            position: "relative",
            border: `1px solid ${theme.customPalette?.border}`,
            "&:hover": {
              backgroundColor: theme.customPalette?.hover,
              color: "#333333",
            },
          }}
        >
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                <span
                  className="material-symbols-outlined"
                  style={{ marginRight: "8px", fontSize: "20px" }}
                >
                  {isOpen ? "folder_open" : "folder"}
                </span>
                <Typography
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    fontWeight: "bold",
                  }}
                >
                  {folder.name}
                </Typography>
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Stack ml={2} mt={1} spacing={1}>
          {folder.workflows.map((workflow) => (
            <Fragment key={workflow.id}>
              {workflowItemRenderer({ workflow })}
            </Fragment>
          ))}
        </Stack>
      </Collapse>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: -10,
          horizontal: 30,
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "160px",
          },
        }}
      >
        <MenuItem onClick={handleAddRule}>ルールを追加する</MenuItem>
        <MenuItem onClick={handleEditClick}>編集</MenuItem>
        <MenuItem sx={{ color: "#FF0000" }} onClick={handleDeleteClick}>
          削除
        </MenuItem>
      </Menu>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 1)",
        }}
        open={isDeleteFolderPopupOpen || isEditFolderNamePopupOpen}
      >
        {isDeleteFolderPopupOpen && (
          <DeleteFolderPopup
            folderId={folder.id}
            selectedFolderLabel={folder.name}
            onClose={handleCloseDeleteFolderPopup}
          />
        )}
        {isEditFolderNamePopupOpen && (
          <EditFolderNamePopup
            folderId={folder.id}
            folderLabel={folder.name}
            onClose={handleCloseEditFolderNamePopup}
          />
        )}
      </Backdrop>
    </Box>
  );
};
